/**
 * Voice2Evidence — Evidence Intelligence Summary (Bug 3.4 · 2026-02).
 *
 * The "Discussion" section of the engagement: an executive narrative +
 * headline findings + top 3 strategic recommendations, written by the
 * methodology team with a Claude Sonnet 4.6 draft assist.
 *
 * Renders as the FIRST section of the Executive Briefing tab. Visible to
 * every authed user (client / reviewer / admin) once published. Admins
 * see an inline authoring drawer (generate draft, edit, publish).
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Sparkles, Loader2, Save, RefreshCw, X, FileText, Trash2, Edit3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Read-only render (client / reviewer view) ─────────────────────

function SummaryDisplay({ summary, engagement }) {
  if (!summary || !summary.narrative) return null;
  return (
    <div className="border border-[#E5DFCF] bg-white rounded-sm shadow-[0_1px_0_rgba(10,17,24,0.03)]" data-testid="evidence-summary-display">
      <div className="px-6 pt-6 pb-4 border-b border-[#F0EBDC]">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#C84C21]">Evidence intelligence summary</div>
        <h2 className="mt-1 font-serif text-[28px] leading-tight text-[#0A1118]">
          What the evidence collectively says
        </h2>
        <div className="mt-1 text-[12.5px] text-[#5C6470]">
          Executive Discussion synthesis · {(summary.narrative || "").split(/\s+/).length} words
          {summary.last_edited_by && ` · last edited by ${summary.last_edited_by}`}
        </div>
      </div>

      {/* Narrative */}
      <div className="px-6 py-6 border-b border-[#F0EBDC]">
        <p className="text-[15px] leading-[1.75] text-[#0F1825] font-serif whitespace-pre-wrap">
          {summary.narrative}
        </p>
      </div>

      {/* Headline findings */}
      {(summary.headline_findings || []).length > 0 && (
        <div className="px-6 py-6 border-b border-[#F0EBDC]">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] mb-3">
            Headline findings · {summary.headline_findings.length}
          </div>
          <ul className="space-y-2.5">
            {summary.headline_findings.map((b, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-[3px] font-mono text-[10px] text-[#C84C21] shrink-0 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[13.5px] leading-relaxed text-[#0F1825]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strategic recs */}
      {(summary.strategic_recs || []).length > 0 && (
        <div className="px-6 py-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] mb-3">
            Top 3 strategic recommendations · ordered by leverage
          </div>
          <div className="space-y-4">
            {summary.strategic_recs.map((r, i) => (
              <div key={i} className="border-l-2 border-[#C84C21] pl-4">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-[10px] text-[#C84C21] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif text-[17px] text-[#0A1118] leading-tight">{r.title}</h3>
                  {r.owner_team && (
                    <span className="ml-auto font-mono text-[9.5px] uppercase tracking-[0.16em] border border-[#E5DFCF] bg-[#FAF7F1] text-[#5C6470] px-2 py-0.5">
                      {r.owner_team}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-[#0F1825] whitespace-pre-wrap">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Empty state (admin & reviewer see different messages) ────────

function SummaryEmptyState({ isAdmin, onOpenAuthor }) {
  return (
    <div className="border border-dashed border-[#D9CFB8] bg-[#FAF7F1] px-6 py-10 rounded-sm text-center" data-testid="evidence-summary-empty">
      <div className="w-12 h-12 mx-auto rounded-full bg-white border border-[#D9CFB8] flex items-center justify-center mb-3">
        <Sparkles size={20} className="text-[#C84C21]" />
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-2">
        Evidence intelligence summary
      </div>
      <h3 className="font-serif text-[20px] text-[#0A1118] leading-tight">Not yet published</h3>
      <p className="mt-2 text-[12.5px] text-[#5C6470] italic max-w-lg mx-auto leading-snug">
        {isAdmin
          ? "This is the Discussion section — a 300-500 word executive narrative + 5-7 headline findings + the top 3 strategic recommendations. Draft with Claude, edit, then publish."
          : "The V2E methodology team is preparing the executive synthesis for this engagement. You'll see it here once it's published."}
      </p>
      {isAdmin && (
        <Button
          onClick={onOpenAuthor}
          className="mt-5 bg-[#C84C21] hover:bg-[#A83E1B] text-white"
          data-testid="evidence-summary-author-cta"
        >
          <Sparkles size={14} className="mr-1.5" /> Draft with Claude
        </Button>
      )}
    </div>
  );
}


// ─── Admin authoring drawer ──────────────────────────────────────

function AuthoringDrawer({ engagementId, initial, onSaved, onClose }) {
  const [narrative, setNarrative]       = useState(initial?.narrative || "");
  const [bullets, setBullets]           = useState(initial?.headline_findings || []);
  const [recs, setRecs]                 = useState(initial?.strategic_recs || []);
  const [generating, setGenerating]     = useState(false);
  const [saving, setSaving]             = useState(false);

  const generate = async () => {
    if (narrative.trim() && !window.confirm("This will REPLACE the current draft with a fresh Claude generation. Continue?")) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/console/engagements/${engagementId}/generate-evidence-summary`,
        {},
        { withCredentials: true },
      );
      const draft = data.draft || {};
      setNarrative(draft.narrative || "");
      setBullets(draft.headline_findings || []);
      setRecs(draft.strategic_recs || []);
      toast.success("Claude draft ready — review + edit, then Publish.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Draft generation failed."));
    } finally {
      setGenerating(false);
    }
  };

  const save = async ({ publish }) => {
    if (!narrative.trim() || narrative.trim().length < 100) {
      toast.error("Narrative must be at least 100 characters.");
      return;
    }
    const filteredBullets = bullets.map((b) => (b || "").trim()).filter(Boolean);
    if (filteredBullets.length < 3) {
      toast.error("At least 3 headline findings required.");
      return;
    }
    const filteredRecs = recs
      .filter((r) => (r?.title || "").trim() && (r?.body || "").trim())
      .map((r) => ({ title: r.title.trim(), body: r.body.trim(), owner_team: (r.owner_team || "").trim() }));
    if (filteredRecs.length < 1) {
      toast.error("At least 1 strategic recommendation required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/console/engagements/${engagementId}/evidence-summary`,
        {
          narrative: narrative.trim(),
          headline_findings: filteredBullets,
          strategic_recs: filteredRecs,
          published: publish,
        },
        { withCredentials: true },
      );
      toast.success(publish ? "Published — reviewers can now see this summary." : "Draft saved.");
      onSaved(data.summary);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Bullet + rec helpers
  const updateBullet = (i, v) => setBullets((prev) => prev.map((b, idx) => idx === i ? v : b));
  const addBullet    = () => setBullets((prev) => [...prev, ""]);
  const removeBullet = (i) => setBullets((prev) => prev.filter((_, idx) => idx !== i));
  const updateRec    = (i, field, v) => setRecs((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: v } : r));
  const addRec       = () => setRecs((prev) => [...prev, { title: "", body: "", owner_team: "" }]);
  const removeRec    = (i) => setRecs((prev) => prev.filter((_, idx) => idx !== i));

  const words = narrative.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0A1118]/60 backdrop-blur-sm flex items-start justify-center py-6 px-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl bg-white rounded-sm shadow-[0_20px_60px_-15px_rgba(10,17,24,0.35)] my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="evidence-summary-authoring-drawer"
      >
        <div className="border-b border-[#E5DFCF] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#C84C21]">Evidence intelligence summary</div>
            <div className="mt-1 font-serif text-[22px] leading-tight text-[#0A1118]">Author the executive Discussion</div>
          </div>
          <button onClick={onClose} className="text-[#A09680] hover:text-[#C84C21]" data-testid="evidence-summary-close">
            <X size={18} />
          </button>
        </div>

        {/* Claude draft CTA */}
        <div className="px-6 py-4 border-b border-[#F0EBDC] bg-[#FAF7F1] flex flex-wrap items-center justify-between gap-3">
          <div className="text-[12px] text-[#5C6470] max-w-2xl leading-snug">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#C84C21] mr-2">Draft assist</span>
            Claude Sonnet 4.6 will compose a 300-500 word Discussion from the engagement&apos;s findings + recommendations. Review, edit, then publish.
          </div>
          <Button
            onClick={generate}
            disabled={generating}
            variant="outline"
            className="border-[#C84C21] text-[#C84C21] hover:bg-[#C84C21]/5"
            data-testid="evidence-summary-generate"
          >
            {generating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : (narrative ? <RefreshCw size={14} className="mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />)}
            {generating ? "Drafting with Claude…" : (narrative ? "Regenerate draft" : "Draft with Claude")}
          </Button>
        </div>

        {/* Narrative editor */}
        <div className="px-6 py-5 border-b border-[#F0EBDC]">
          <div className="flex items-center justify-between mb-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680]">Executive narrative · 300-500 words</label>
            <span className={`font-mono text-[10px] ${
              words < 200 ? "text-[#8C3A12]" : words > 700 ? "text-[#8C3A12]" : "text-[#2C5C42]"
            }`}>
              {words} words
            </span>
          </div>
          <Textarea
            value={narrative}
            onChange={(e) => setNarrative(e.target.value)}
            rows={16}
            className="bg-white border-[#D9CFB8] text-[13.5px] leading-relaxed font-serif"
            placeholder="A 300-500 word executive Discussion. Third-person, precise, anchored in specific findings, no headings or bullets. Close with what this means for the client's decision."
            data-testid="evidence-summary-narrative"
            maxLength={6000}
          />
        </div>

        {/* Headline findings editor */}
        <div className="px-6 py-5 border-b border-[#F0EBDC]">
          <div className="flex items-center justify-between mb-3">
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680]">Headline findings · 5-7 bullets</label>
            <button
              onClick={addBullet}
              disabled={bullets.length >= 8}
              className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[#C84C21] hover:underline disabled:opacity-40"
              data-testid="evidence-summary-add-bullet"
            >+ Add bullet</button>
          </div>
          <div className="space-y-2">
            {bullets.length === 0 && (
              <div className="text-[11.5px] italic text-[#7A6244]">
                Draft with Claude to auto-populate, or add bullets manually.
              </div>
            )}
            {bullets.map((b, i) => (
              <div key={i} className="flex gap-2 items-start">
                <span className="mt-2 font-mono text-[10px] text-[#C84C21] tabular-nums w-6 text-center shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <Textarea
                  value={b}
                  onChange={(e) => updateBullet(i, e.target.value)}
                  rows={2}
                  className="flex-1 bg-white border-[#D9CFB8] text-[12.5px] leading-snug"
                  placeholder="One-sentence headline finding, ≤ 240 chars."
                  maxLength={260}
                  data-testid={`evidence-summary-bullet-${i}`}
                />
                <button
                  onClick={() => removeBullet(i)}
                  className="mt-2 text-[#A09680] hover:text-[#C84C21] shrink-0"
                  aria-label="Remove bullet"
                  data-testid={`evidence-summary-bullet-remove-${i}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Strategic recs editor */}
        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680]">Top 3 strategic recommendations · ordered by leverage</label>
            <button
              onClick={addRec}
              disabled={recs.length >= 5}
              className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[#C84C21] hover:underline disabled:opacity-40"
              data-testid="evidence-summary-add-rec"
            >+ Add rec</button>
          </div>
          <div className="space-y-4">
            {recs.map((r, i) => (
              <div key={i} className="border-l-2 border-[#C84C21] pl-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-[#C84C21] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <input
                    value={r.title || ""}
                    onChange={(e) => updateRec(i, "title", e.target.value)}
                    placeholder="Rec headline (≤ 90 chars)"
                    maxLength={120}
                    className="flex-1 border-b border-[#D9CFB8] bg-white text-[14px] font-serif py-1 focus:outline-none focus:border-[#C84C21]"
                    data-testid={`evidence-summary-rec-title-${i}`}
                  />
                  <input
                    value={r.owner_team || ""}
                    onChange={(e) => updateRec(i, "owner_team", e.target.value)}
                    placeholder="Owner team"
                    maxLength={60}
                    className="w-40 border-b border-[#D9CFB8] bg-white text-[11.5px] font-mono uppercase tracking-[0.12em] py-1 focus:outline-none focus:border-[#C84C21]"
                    data-testid={`evidence-summary-rec-team-${i}`}
                  />
                  <button onClick={() => removeRec(i)} className="text-[#A09680] hover:text-[#C84C21]" aria-label="Remove rec">
                    <Trash2 size={13} />
                  </button>
                </div>
                <Textarea
                  value={r.body || ""}
                  onChange={(e) => updateRec(i, "body", e.target.value)}
                  rows={3}
                  className="bg-white border-[#D9CFB8] text-[12.5px] leading-relaxed"
                  placeholder="80-140 words — WHY this rec, WHAT to do, WHICH team executes."
                  maxLength={1500}
                  data-testid={`evidence-summary-rec-body-${i}`}
                />
              </div>
            ))}
            {recs.length === 0 && (
              <div className="text-[11.5px] italic text-[#7A6244]">
                Draft with Claude to auto-populate, or add recommendations manually.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5DFCF] px-6 py-4 bg-[#FAF7F1] flex flex-wrap items-center justify-between gap-3">
          <div className="text-[10.5px] font-mono text-[#A09680]">
            Every save is timestamped + attributed to your account
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => save({ publish: false })}
              disabled={saving}
              variant="outline"
              className="border-[#D9CFB8] text-[#5C6470]"
              data-testid="evidence-summary-save-draft"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
              Save as draft
            </Button>
            <Button
              onClick={() => save({ publish: true })}
              disabled={saving}
              className="bg-[#2C5C42] hover:bg-[#22452F] text-white"
              data-testid="evidence-summary-publish"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <FileText size={14} className="mr-1.5" />}
              Save &amp; publish
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Public wrapper — top of Executive Briefing tab ────────────────

export function EvidenceIntelligenceSummary({ engagementId, engagement, isAdmin }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/console/engagements/${engagementId}/evidence-summary`,
        { withCredentials: true },
      );
      setSummary(data.summary || null);
    } catch {
      // Silent — the tab will show the empty state
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="border border-[#E5DFCF] bg-[#FAF7F1] px-6 py-6 rounded-sm">
        <div className="flex items-center gap-2 text-[#5C6470] text-[13px]">
          <Loader2 size={14} className="animate-spin" /> Loading Evidence Intelligence Summary…
        </div>
      </div>
    );
  }

  const isPublished = !!(summary && summary.narrative && summary.published);

  return (
    <div className="space-y-2 mb-8" data-testid="evidence-summary-section">
      {isPublished ? (
        <SummaryDisplay summary={summary} engagement={engagement} />
      ) : (
        <SummaryEmptyState isAdmin={isAdmin} onOpenAuthor={() => setDrawer(true)} />
      )}

      {/* Admin edit affordance when published */}
      {isPublished && isAdmin && (
        <div className="flex justify-end pt-2">
          <button
            onClick={() => setDrawer(true)}
            className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#C84C21] hover:underline flex items-center gap-1.5"
            data-testid="evidence-summary-edit"
          >
            <Edit3 size={12} /> Edit summary
          </button>
        </div>
      )}

      {drawer && (
        <AuthoringDrawer
          engagementId={engagementId}
          initial={summary}
          onSaved={(fresh) => { setSummary(fresh); setDrawer(false); }}
          onClose={() => setDrawer(false)}
        />
      )}
    </div>
  );
}
